import { useTranslation, Trans } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { ArrowUpRight, MapPin, Mail, Phone, Send, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import heroImg from "@/assets/hero-drilling.jpg";
import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";

function Reveal({ children, delay = 0, direction = "up", className = "" }: { children: React.ReactNode; delay?: number; direction?: "up" | "left" | "right"; className?: string }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-8%" });
    const hidden = direction === "up" ? { opacity: 0, y: 40 } : direction === "left" ? { opacity: 0, x: 50 } : { opacity: 0, x: -50 };
    const visible = direction === "up" ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 };
    return (
        <motion.div ref={ref} className={className} initial="hidden" animate={inView ? "visible" : "hidden"}
            variants={{ hidden, visible: { ...visible, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } } }}>
            {children}
        </motion.div>
    );
}

export default function Contact() {
    const { t } = useTranslation("contact");
    const projectOptions = t("form.project_options", { returnObjects: true }) as string[];

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        company: '',
        interest: 'surface',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [submitMessage, setSubmitMessage] = useState('');

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const response = await fetch('/send-mail.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (data.success) {
                setSubmitStatus('success');
                setSubmitMessage(data.message);
                setFormData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    company: '',
                    interest: 'surface',
                    message: ''
                });
            } else {
                setSubmitStatus('error');
                setSubmitMessage(data.message || 'Une erreur est survenue');
            }
        } catch (error) {
            setSubmitStatus('error');
            setSubmitMessage('Erreur de connexion. Veuillez réessayer.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Contact & Devis — Salmamy Drill | Forage Minier et Hydraulique</title>
                <meta name="description" content="Contactez Salmamy Drill pour tout projet de forage de surface, souterrain ou d'eau. Études techniques sous 48h. Bureau au Mali, service Afrique de l'Ouest." />
                <meta name="keywords" content="contact salmamy drill, devis forage, étude technique forage, Mali, Afrique de l'Ouest, téléphone forage, email forage" />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="Contact — Salmamy Drill" />
                <meta property="og:description" content="Contactez Salmamy Drill pour vos projets de forage. Études techniques sous 48h." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://salmamydrill.com/contact" />
                <meta property="og:image" content={heroImg} />
                <meta property="og:locale" content="fr_FR" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Contact — Salmamy Drill" />
                <meta name="twitter:description" content="Contactez Salmamy Drill pour vos projets de forage. Études techniques sous 48h." />
                <meta name="twitter:image" content={heroImg} />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Inter:wght@400;500;600&display=swap" />
            </Helmet>
            <div className="min-h-screen bg-background text-foreground flex flex-col overflow-x-hidden">
                <Nav />

                {/* Dark Premium Hero */}
                <section className="relative bg-surface text-surface-foreground pt-32 pb-24 md:pt-48 md:pb-32 px-6 grain overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-b from-surface via-surface to-background/5" />
                    <motion.div initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 0.2, scale: 1 }} transition={{ duration: 2.2, ease: "easeOut" }} className="absolute right-0 top-0 w-1/2 h-full pointer-events-none mix-blend-screen">
                        <img src={heroImg} alt="Forage Background" className="w-full h-full object-cover mask-image-l" />
                    </motion.div>

                    <div className="relative z-10 mx-auto max-w-7xl flex flex-col items-start">
                        <motion.span className="eyebrow inline-flex items-center gap-3 text-primary tracking-[0.3em] font-bold text-xs uppercase mb-8" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}>
                            <span className="w-12 h-px bg-primary/40 block"></span>
                            {t("hero.eyebrow")}
                        </motion.span>
                        <div className="overflow-hidden mb-4">
                            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}>
                                <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] font-display font-black leading-[0.9] tracking-tighter uppercase text-surface-foreground">
                                    {t("hero.h1_line1")} <br />
                                    <span className="text-primary italic">{t("hero.h1_accent")}</span>
                                </h1>
                            </motion.div>
                        </div>
                        <motion.p className="text-xl text-surface-foreground/70 max-w-2xl leading-relaxed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease: [0.16, 1, 0.3, 1] }} dangerouslySetInnerHTML={{ __html: t("hero.subtitle") }} />
                    </div>
                </section>

                <main className="flex-1 pb-32">
                    <div className="mx-auto max-w-7xl px-6 mt-[-4rem] relative z-20">
                        <div className="grid lg:grid-cols-5 gap-12 items-start">

                            {/* Contact Form Container (Spans 3 cols) */}
                            <Reveal direction="left" className="lg:col-span-3">
                                <div className="bg-white border border-border shadow-2xl p-8 md:p-14">
                                    <h2 className="font-display text-3xl font-black uppercase mb-8 flex items-center gap-3">
                                        {t("form.title")} <span className="w-10 h-1 bg-primary inline-block ml-4"></span>
                                    </h2>

                                    {submitStatus === 'success' && (
                                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                                            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                                            <p className="text-green-800 text-sm">{submitMessage}</p>
                                        </div>
                                    )}

                                    {submitStatus === 'error' && (
                                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                                            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                                            <p className="text-red-800 text-sm">{submitMessage}</p>
                                        </div>
                                    )}

                                    <form className="space-y-8" onSubmit={handleSubmit}>
                                        <div className="grid grid-cols-2 gap-8">
                                            <div className="space-y-3">
                                                <label htmlFor="first_name" className="text-xs uppercase font-bold tracking-widest text-foreground/50">{t("form.first_name")}</label>
                                                <input
                                                    type="text"
                                                    id="first_name"
                                                    name="first_name"
                                                    value={formData.first_name}
                                                    onChange={handleInputChange}
                                                    className="w-full bg-transparent border-b-2 border-border px-0 py-2 text-foreground font-medium focus:outline-none focus:border-primary transition-colors"
                                                    placeholder={t("form.first_name_placeholder")}
                                                    required
                                                />
                                            </div>
                                            <div className="space-y-3">
                                                <label htmlFor="last_name" className="text-xs uppercase font-bold tracking-widest text-foreground/50">{t("form.last_name")}</label>
                                                <input
                                                    type="text"
                                                    id="last_name"
                                                    name="last_name"
                                                    value={formData.last_name}
                                                    onChange={handleInputChange}
                                                    className="w-full bg-transparent border-b-2 border-border px-0 py-2 text-foreground font-medium focus:outline-none focus:border-primary transition-colors"
                                                    placeholder={t("form.last_name_placeholder")}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="space-y-3">
                                            <label htmlFor="email" className="text-xs uppercase font-bold tracking-widest text-foreground/50">{t("form.email")}</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full bg-transparent border-b-2 border-border px-0 py-2 text-foreground font-medium focus:outline-none focus:border-primary transition-colors"
                                                placeholder={t("form.email_placeholder")}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <label htmlFor="company" className="text-xs uppercase font-bold tracking-widest text-foreground/50">{t("form.company")}</label>
                                            <input
                                                type="text"
                                                id="company"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleInputChange}
                                                className="w-full bg-transparent border-b-2 border-border px-0 py-2 text-foreground font-medium focus:outline-none focus:border-primary transition-colors"
                                                placeholder={t("form.company_placeholder")}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-3">
                                            <label htmlFor="interest" className="text-xs uppercase font-bold tracking-widest text-foreground/50">{t("form.project_type")}</label>
                                            <select
                                                id="interest"
                                                name="interest"
                                                value={formData.interest}
                                                onChange={handleInputChange}
                                                className="w-full bg-transparent border-b-2 border-border px-0 py-2 text-foreground font-medium focus:outline-none focus:border-primary transition-colors appearance-none cursor-pointer"
                                                required
                                            >
                                                {projectOptions.map((opt: string, idx: number) => {
                                                    const valueMap = ["surface", "underground", "water", "other"];
                                                    return <option key={idx} value={valueMap[idx]}>{opt}</option>;
                                                })}
                                            </select>
                                        </div>
                                        <div className="space-y-3">
                                            <label htmlFor="message" className="text-xs uppercase font-bold tracking-widest text-foreground/50">{t("form.details")}</label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                value={formData.message}
                                                onChange={handleInputChange}
                                                rows={4}
                                                className="w-full bg-transparent border-b-2 border-border px-0 py-2 text-foreground font-medium focus:outline-none focus:border-primary transition-colors resize-none"
                                                placeholder={t("form.details_placeholder")}
                                                required
                                            ></textarea>
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="mt-4 flex items-center justify-between w-full bg-surface text-surface-foreground px-8 py-5 text-sm font-bold tracking-widest uppercase hover:bg-primary hover:text-primary-foreground transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span>{isSubmitting ? t("form.submit_loading") : t("form.submit")}</span>
                                            {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                                        </button>
                                    </form>
                                </div>
                            </Reveal>

                            {/* Direct Info (Spans 2 cols) */}
                            <Reveal direction="right" delay={0.2} className="lg:col-span-2 space-y-12 lg:pt-14">
                                <div className="bg-surface/5 p-8 border-l-4 border-primary">
                                    <h3 className="text-xl font-display font-black uppercase mb-6 text-foreground">{t("info.office_title")}</h3>
                                    <div className="space-y-5">
                                        <div className="flex gap-4">
                                            <MapPin className="w-6 h-6 text-primary shrink-0" />
                                            <div>
                                                <p className="font-bold text-foreground">Sotuba ACI, route de la corniche, à côté du 3ème pont, porte 07</p>
                                                <p className="text-foreground/70 font-medium">Bamako, Mali</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-4 items-center">
                                            <Mail className="w-6 h-6 text-primary shrink-0" />
                                            <p className="font-bold text-foreground">contact@salmamydrill.com</p>
                                        </div>
                                        <div className="flex gap-4 items-center">
                                            <Phone className="w-6 h-6 text-primary shrink-0" />
                                            <p className="font-bold text-foreground flex flex-col">
                                                <span>+223 20 21 75 02</span>
                                                <span className="text-xs text-foreground/50 font-normal">{t("info.hours")}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-surface/5 p-8 border-l-4 border-surface">
                                    <h3 className="text-xl font-display font-black uppercase mb-6 text-foreground">{t("info.base_title")}</h3>
                                    <div className="space-y-5">
                                        <div className="flex gap-4">
                                            <MapPin className="w-6 h-6 text-surface shrink-0" />
                                            <div>
                                                <p className="font-bold text-foreground">Sotuba ACI, route de la corniche, à côté du 3ème pont, porte 07</p>
                                                <p className="text-foreground/70 font-medium">Bamako, Mali</p>
                                                <p className="text-xs text-primary font-bold mt-1 uppercase tracking-wider">{t("info.fleet_note")}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-4 items-center">
                                            <Phone className="w-6 h-6 text-surface shrink-0" />
                                            <p className="font-bold text-foreground flex flex-col">
                                                <span>+223 20 21 75 02</span>
                                                <span className="text-xs text-foreground/50 font-normal">{t("info.coordination")}</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
}
